// @flow
import { createSelector } from 'reselect';
import type { State } from 'services/reducers/index';
import { calculateCartItemTotal } from '../utils/currency';

import type { CartVariant, StateProductData } from '../sharedTypes';

export const selectCartVariants: (State) => Array<CartVariant> = createSelector(
  (state: State) => state.cartState.variantIds,
  (state: State) => state.cartState.variantQuantities,
  (state: State) => state.productsState.variants,
  (state: State) => state.productsState.products,
  (state: State) => state.cartState.variantIdsBeingUpdated,
  (state: State) => state.cartState.prevPurchasedVariantQuantities,
  (
    variantIds,
    variantQuantities,
    variants,
    products,
    variantIdsBeingUpdated,
    prevPurchasedVariantQuantities
  ) => {
    return variantIds
      .map((variantId) => {
        const variant = variants[variantId];
        // cart details (where the list of ids comes from) and variants/products are not updated at the exact same time
        // there is a small chance that the variant is not found
        if (!variant) return {};
        const product = products[variant.parentId];
        const { salePrice, price } = variant;
        const quantity = variantQuantities[variantId];
        const prevPurchasedCount =
          prevPurchasedVariantQuantities[variantId] || 0;
        const { cartLimit } = variants[variantId];
        const newCartLimit = cartLimit - prevPurchasedCount;
        const isVariantAtPurchaseLimit = newCartLimit === 0;

        return {
          ...variant,
          areCartOperationsDisabled: variantIdsBeingUpdated.has(variantId),
          id: variantId,
          name: product.name,
          gwp: product.gwp,
          quantity,
          quantityUpdating: variantIdsBeingUpdated.has(variantId), // determines button loading state
          shareUrl: product.shareUrl,
          itemTotal: calculateCartItemTotal(quantity, salePrice, price),
          isPreviouslyPurchased: prevPurchasedCount > 0,
          isVariantAtPurchaseLimit,
          cartLimit: newCartLimit,
          sponsored: product.sponsored,
          shippingExempt: product.shippingExempt,
        };
      })
      .sort((a) => (a.gwp ? -1 : 0))
      .filter((variant) => variant.quantity > 0);
  }
);

export const selectGwpProductsInCart: (
  state: State
) => StateProductData = createSelector(
  (state: State) => state.cartState.variantIds,
  (state: State) => state.productsState.variants,
  (state: State) => state.productsState.products,
  (variantIds, variants, products) => {
    return variantIds
      .map((variantId) => {
        const variant = variants[variantId];
        return products[variant.parentId];
      })
      .filter((p) => p.gwpThreshold > 0)[0];
  }
);

export const selectSubtotal: (State) => number = createSelector(
  selectCartVariants,
  (cartVariants) => {
    let total = 0;

    cartVariants.forEach((variant) => {
      const { price, salePrice, quantity } = variant;
      const priceInCents = 100 * parseFloat(salePrice || price);
      total += priceInCents * quantity;
    });

    return total;
  }
);

export type SelectedCredit = {|
  remainingCredit: string,
  initialAddonCredit: string,
  initialPurchaseCredit: string,
  appliedAddonCredit: string,
  appliedPurchaseCredit: string,
|};

export const selectIsCartEmpty: (State) => boolean = createSelector(
  (state) => state.cartState.variantQuantities,
  (variantQuantities) => {
    for (const id in variantQuantities) {
      if (variantQuantities[id] > 0) return false;
    }
    return true;
  }
);

export const selectHasSponsoredProductInCart: (State) => boolean = createSelector(
  selectCartVariants,
  (cartVariants) => cartVariants.some((variant) => variant.sponsored)
);
